import { Obj, IAction, IState, IRect, IProduct, PositionMutation, IHolder, ISide, IUrlData } from "./types"
import * as  CanvasRender from "./renderer"
import m from "mithril"
import { isArabicText, getIndex, constrainPosition, snapPosition } from "../../utils"
import localForage from "localforage"
import { debounce, createImage, createCanvas, UUID, pause, mmToPx, getDpi, pxToMm } from "oj-utils"
import { EventAggregator } from "oj-eventaggregator"
import * as ProductConfig from "./products"
import { uploadFile, createCartLine, updateCartLine, getCart } from "./api"

const state: IState = {
  urlData: undefined,
  loaded: false,
  productIndex: 0,
  products: [],
  alert: undefined,
}

export const ea = new EventAggregator()

const parseUrlData = <T extends {}>(str = location.search) => {
  const data = {} as T
  const pairs = (str[0] === '?' ? str.substr(1) : str).split('&')
  for (let i = 0; i < pairs.length; i++) {
    const pair = pairs[i].split('=')
    data[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '')
  }
  return data
}

export const load = async () => {
  const parsedUrlData = parseUrlData<{ state: string, editorId?: string }>()
  state.urlData = ProductConfig.parseUrlState(parsedUrlData.state || "eyJwcm9kdWN0Ijp7ImlkIjo0NTQ5MjU2ODM5Mjk5LCJ0aXRsZSI6Ik5vdGVib29rIFBvY2tldCBUb3AgSGFyZGNvdmVyIn0sInZhcmlhbnQiOnsiaWQiOjMyMTgwMzIzMzg1NDc1LCJ0aXRsZSI6IlBvY2tldCAoOTB4MTQwKSAvIEJsdWUgLyBGdWxsY29sb3IifSwicXVhbnRpdHkiOjF9")
  state.urlData.editorId = parsedUrlData.editorId
  state.products = [ProductConfig.loadProductConfig(state.urlData)]

  // for (const p of state.products)
  await restoreProduct(getProduct())
}

export const getUrlData = () =>
  state.urlData

export const updateUrlData = (update: Partial<IUrlData>) =>
  Object.assign(state.urlData, update)

export const acceptedFileTypes = () =>
  state.urlData.embossed
    ? ["application/pdf", "application/postscript"]
    : ["image/jpeg", "image/png", "image/gif", "application/pdf", "application/postscript"]

interface IStoredProduct {
  sides: {
    holders: { type: IHolder["type"], position: IHolder["position"], actions: { key: string, value: number | string | boolean, asset?: string }[] }[]
  }[]
}

export const getCurrentHash = (data = getUrlData()) =>
  state.urlData.editorId || `${data.product.id}_${data.variant.id}`

const storeProductDebounced = debounce((p?: IProduct, k?: string) => storeProduct(p, k), 500)
export const storeProduct = async (product = getProduct(), key = getCurrentHash()) => {
  if (!isLoaded())
    return

  const stripped: IStoredProduct = {
    sides: product.sides
      .map(x =>
        ({
          holders: x.holders.map(({ type, position, actions }) =>
            ({
              type,
              position,
              actions: actions.filter(({ key }) => !["depth", "delete"].includes(key)).map(({ key, value, asset }) =>
                ({
                  key,
                  value: key === "image" ? value?.src ?? undefined : value,
                  asset
                }))
            })
          )
        })
      ),
  }

  await localForage.setItem(key, stripped)
}

export const restoreProduct = async (product = getProduct(), key = getCurrentHash()) => {
  const stored = await localForage.getItem<IStoredProduct>(key)

  if (!stored) {
    state.loaded = true
    return
  }

  for (let sideIndex = 0; sideIndex < stored.sides.length; sideIndex++) {
    const { holders } = stored.sides[sideIndex]

    for (const { type, position, actions } of holders) {
      if (state.urlData.embossed && type !== "image")
        continue

      const holder = addHolder(type, product.sides[sideIndex])
      holder.position.rotation = position.rotation
      setPosition(position, "move", holder)
      for (const { key, value, asset } of actions) {
        if (key === "image" && typeof value === "string") {
          const img = await createImage(value)
          setActionValue(img, holder, key)
          if (asset)
            setActionValueAsset(asset, holder, key)
        }
        else
          setActionValue(value, holder, key)
      }
    }
  }

  state.loaded = true
  const side = setSide(0, product)
  side.renderer?.render()
  m.redraw()
  await pause(1000)
  side.renderer?.setDirty(true).render()
  m.redraw()
}

export const clearProduct = async (product: IProduct, key = getCurrentHash()) => {
  await localForage.removeItem(key)
}

export const isLoaded = () =>
  state.loaded

export const getProduct = (): IProduct =>
  state.products[state.productIndex]

export const getSide = (product = getProduct()) =>
  product.sides[product.sideIndex]

export const getHolder = (holderIndex = getSide().holderIndex, side = getSide()) =>
  side.holders[holderIndex]

export const getAction = (indexOrKey?: number | string, holder = getHolder()): IAction =>
  holder.actions[indexOrKey === undefined ? holder.actionIndex : getIndex(indexOrKey, holder.actions)]

export const setSide = (sideIndex: number, product = getProduct()) => {
  product.sideIndex = sideIndex
  const side = getSide(product)
  side.holderIndex = undefined
  return side
}

export const setSidePrev = (product = getProduct()) => {
  let i = product.sideIndex - 1
  if (i < 0)
    i = product.sides.length - 1
  setSide(i, product)
}

export const setSideNext = (product = getProduct()) => {
  let i = product.sideIndex + 1
  if (i > product.sides.length - 1)
    i = 0
  setSide(i, product)
}

export const setProduct = (productIndex: number) => {
  state.productIndex = productIndex
}

export const setProductPrev = () => {
  let i = state.productIndex - 1
  if (i < 0)
    i = state.products.length - 1
  setProduct(i)
}

export const setProductNext = () => {
  let i = state.productIndex + 1
  if (i > state.products.length - 1)
    i = 0
  setProduct(i)
}

export const setDialog = (msg?: IState["alert"]) =>
  state.alert = msg

export const getAlert = () =>
  state.alert

export const setAction = (indexOrKey: number | string, holder = getHolder()) => {
  if (!holder)
    return

  holder.actionIndex = getIndex(indexOrKey, holder.actions)
}

export const setHolder = (holderIndex?: number, side = getSide()) => {
  const prevHolder = side.holders[side.holderIndex]
  side.holderIndex = holderIndex
  const h = side.holders[side.holderIndex]
  if (!h)
    return h

  h.actionIndex = (prevHolder && prevHolder.type === h.type)
    ? prevHolder.actionIndex
    : 0 // todo is this correct behaviour?
  return h
}

export const addHolder = (type: "image" | "text", side = getSide()) => {
  const holder = ProductConfig.createHolder(type, side)
  const i = side.holders.push(holder) - 1
  setHolder(i, side)
  updateDepthAction(side)

  if (isLoaded()) {
    redraw(side)
    storeProductDebounced(side.product)
  }

  return holder
}

export const deleteHolder = (holder = getHolder()) => {
  const i = holder.side.holders.indexOf(holder)
  if (i === -1)
    return

  holder.side.holders.splice(i, 1)
  holder.side.holderIndex = holder.side.holders.length > 0 ? Math.max(0, i - 1) : undefined
  updateDepthAction(holder.side)

  if (isLoaded()) {
    redraw(holder.side)
    storeProductDebounced(holder.side.product)
  }
}

export const createHolderBackup = (holder = getHolder()) => {
  const position = { ...holder.position }
  const actions = holder.actions.map(({ key, value }) => ({ key, value })).filter(x => !["depth", "delete"].includes(x.key))
  holder.cache.backup = { position, actions }
}

export const restoreHolderBackup = (holder = getHolder()) => {
  if (!holder.cache.backup)
    return

  Object.assign(holder.position, holder.cache.backup.position)
  holder.cache.backup.actions.map(({ key, value }) => setActionValue(value, holder, key))
  delete holder.cache.backup
  holder.dirty = true
  redraw()
}

const updateDepthAction = (side = getSide()) =>
  side.holders.forEach(x => {
    const a = getAction("depth", x)
    if (!a.state)
      a.state = {}

    if (!a.options[0].state)
      a.options[0].state = {}

    if (!a.options[1].state)
      a.options[1].state = {}

    a.state.hidden = side.holders.length < 2
    a.options[0].state.disabled = side.holders.indexOf(x) === 0
    a.options[1].state.disabled = side.holders.indexOf(x) === side.holders.length - 1
  })

export const setActionValuePassive = (value: any, holder = getHolder(), indexOrKey: number | string = holder.actionIndex) => {
  if (!holder)
    return

  const action = getAction(indexOrKey, holder)

  if (action.key === "delete") {
    if (value === "delete")
      deleteHolder(holder)
    return
  }

  else if (action.key === "depth") {
    if (value === "front")
      setDepth(1, holder)
    else
      setDepth(-1, holder)
    return
  }

  else if (action.key === "text") {
    const prevArabic = isArabicText(action.value)
    const nextArabic = isArabicText(value)
    if (prevArabic !== nextArabic) {
      const options = getAction("font", holder).options
      options.forEach(x => x.state.hidden = x.state.arabic !== nextArabic)
      setActionValuePassive(options.find(x => !x.state.hidden).value, holder, "font")
      setTimeout(() => {
        holder.dirty = true
        holder.side.renderer?.render()
      }, 1000)
    }
  }

  action.value = value


  if (isLoaded()) {
    if (action.key === "fit")
      validateImageHolderDpi(holder)
    storeProductDebounced(holder.side.product)
  }
}

export const setActionValue = (value: any | "PENDING", holder = getHolder(), indexOrKey: number | string = holder.actionIndex) => {
  setActionValuePassive(value, holder, indexOrKey)

  holder.dirty = true

  if (isLoaded())
    redraw()
}

export const setActionValueAsset = (asset: string | undefined, holder = getHolder(), indexOrKey: number | string = holder.actionIndex) => {
  const action = getAction(indexOrKey, holder)
  action.asset = asset
}

export const setActionState = (value: Obj<boolean>, holder = getHolder(), indexOrKey: number | string = holder.actionIndex) => {
  if (!holder)
    return

  const action = getAction(indexOrKey, holder)
  if (!action.state)
    action.state = {}
  Object.assign(action.state, value)
}

export const setPosition = (position: Partial<IRect & { rotation: number }>, type: PositionMutation = "all", holder = getHolder(), force?: boolean) => {
  if (!holder)
    return

  let next = { ...holder.position, ...position } as IRect

  const moved = next.x !== holder.position.x || next.y !== holder.position.y
  const resized = next.width !== holder.position.width || next.height !== holder.position.height

  if (!moved && !resized && !force)
    return

  next = constrainPosition(next, holder.side.position, holder.side.margin, type)
  if (type.indexOf("move") !== -1) next = snapPosition(next, holder.side.snapPoints)

  Object.assign(holder.position, { x: next.x, y: next.y, width: next.width, height: next.height })

  holder.dirty = resized

  if (isLoaded()) {
    redraw(holder.side)
    storeProductDebounced(holder.side.product)

    if (resized && holder.type === "image")
      validateImageHolderDpi(holder)
  }
}

export const validateImageHolderDpi = (holder: IHolder) => {
  const { dpi, img } = getImageHolderDpi(holder)
  if (!dpi || !img)
    return

  if (dpi < 250)
    showImgQualityDialog(holder, dpi, img)
  else
    setDialog()
}

export const setImageHolderRecommendedDimensions = (holder: IHolder, { dpi, img } = getImageHolderDpi(holder)) => {
  if (holder.type !== "image" || !img || !dpi || dpi >= 300)
    return

  setPosition({
    width: pxToMm(holder.position.rotation % 2 ? img.naturalHeight : img.naturalWidth, 300),
    height: pxToMm(holder.position.rotation % 2 ? img.naturalWidth : img.naturalHeight, 300)
  }, "all", holder, true)
}

const getImageHolderDpi = (holder: IHolder) => {
  if (holder.type !== "image")
    return { dpi: undefined, img: undefined }

  const imgAction = getAction("image", holder)
  const img = imgAction.value as HTMLImageElement

  if (!img)
    return { dpi: undefined, img: undefined }

  const fitAction = getAction("fit", holder)
  const dpiW = getDpi(img.naturalWidth, holder.position.rotation % 2 ? holder.position.height : holder.position.width)
  const dpiH = getDpi(img.naturalHeight, holder.position.rotation % 2 ? holder.position.width : holder.position.height)
  const dpi = fitAction.value === "contain" ? Math.max(dpiW, dpiH) : Math.min(dpiW, dpiH)

  return { dpi, img }
}

export const setRotation = (dir: -1 | 1, holder = getHolder()) => {
  holder.position.rotation += dir
  if (holder.position.rotation > 3)
    holder.position.rotation = 0
  else if (holder.position.rotation < 0)
    holder.position.rotation = 3

  const rotated = { ...holder.position }
  rotated.x = holder.position.x + (holder.position.width - holder.position.height) * .5
  rotated.y = holder.position.y + (holder.position.height - holder.position.width) * .5
  rotated.width = holder.position.height
  rotated.height = holder.position.width
  setPosition(rotated, "all", holder, true)
}

export const setDepth = (dir: -1 | 1, holder = getHolder()) => {
  const holders = holder.side.holders
  const index = holders.indexOf(holder)
  const next = Math.max(Math.min(holders.length - 1, index + dir), 0)
  if (index === next)
    return index
  holders.splice(next, 0, ...holders.splice(index, 1))
  holder.side.holderIndex = next
  updateDepthAction(holder.side)

  if (isLoaded()) {
    redraw(holder.side)
    storeProductDebounced(holder.side.product)
  }
}

export const redraw = (side = getSide()) => {
  if (side.renderer && isLoaded())
    side.renderer.render()
}

export const setRenderer = async (canvas: HTMLCanvasElement, side = getSide()) => {
  side.renderer = await CanvasRender.load(canvas, side)
  return side.renderer
}

export const isExporting = () =>
  state.exporting

export const sideToImage = async ({ scale, size, type, quality, reset, printOnly, showPlaceholders }: { scale?: number, size?: number, type?: "image/jpeg" | "image/png", quality?: number, reset?: boolean, printOnly?: boolean, showPlaceholders?: boolean } = {}, side: ISide) => {
  if (!side.renderer)
    side.renderer = await setRenderer(document.createElement("canvas"), side)

  const options = side.renderer.getOptions()
  const image = side.renderer
    .setScale(size ? (size / Math.min(side.position.width, side.position.height)) : (scale || 1))
    .setAsync(false)
    .setDirty(true)
    .setPrintOnly(printOnly === undefined ? true : printOnly)
    .setShowPlaceholders(showPlaceholders === undefined ? true : showPlaceholders)
    .render()
    .toImage(type || "image/jpeg", quality || .6)

  if (reset)
    side.renderer // reset
      .setScale(options.scale)
      .setDirty(true)
      .setAsync(options.async)
      .setPrintOnly(options.printOnly)
      .setShowPlaceholders(options.showPlaceholders)
      .render()

  return image
}

export const exportProduct = async (product: IProduct) => {
  state.exporting = true
  m.redraw()

  const thumbs = await Promise.all(product.sides.map(x => sideToImage({
    size: 140,
    type: "image/png",
    printOnly: false,
    showPlaceholders: false
  }, x)))
  const prints = await Promise.all(product.sides.map(x => sideToImage({
    scale: mmToPx(1, 300),
    type: "image/png",
    printOnly: true,
    showPlaceholders: false
  }, x)))

  const assets = (await Promise.all(product.sides
    .flatMap(x => x.holders.flatMap(y => y.actions))
    .filter(x => x.asset)
    .map(x => uploadFile(x.asset))))
    .reduce((s, x, i) => {
      s[`oj_as${i}`] = x
      return s
    }, {})

  console.log({ thumbs, prints, assets })

  const [oj_et0, oj_et1, oj_ep0, oj_ep1] = await Promise.all([...thumbs, ...prints].map(uploadFile))

  if (state.urlData.editorId) {
    const cart = await getCart()
    const cartLine = cart.items.find(x => x.properties.oj_eid === state.urlData.editorId)
    if (!cartLine) {
      state.urlData.editorId = undefined
    }
    else if (cartLine.variant_id !== state.urlData.variant.id) {
      state.urlData.editorId = undefined
      const updatedCart = await updateCartLine(cartLine.key, { quantity: 0, })
    }
    else {
      const updatedCart = await updateCartLine(cartLine.key, { quantity: state.urlData.quantity, properties: { oj_eid: state.urlData.editorId, oj_et0, oj_et1, oj_ep0, oj_ep1, ...assets } })
    }
  }
  if (!state.urlData.editorId) {
    state.urlData.editorId = UUID()
    const cartLine = await createCartLine({
      id: state.urlData.variant.id,
      quantity: state.urlData.quantity,
      properties: { oj_eid: state.urlData.editorId, oj_et0, oj_et1, oj_ep0, oj_ep1, ...assets }
    })
  }

  await Promise.all([
    clearProduct(product),
    storeProduct(product, state.urlData.editorId)
  ])

  const parsedUrlData = parseUrlData<{ state: string }>()
  history.replaceState(null, "edit print", `${location.origin}${location.pathname}?state=${parsedUrlData.state}&editorId=${state.urlData.editorId}`)

  window.location.href = window.location.origin + "/cart"
}

export const toCart = async () => {
  const product = getProduct()

  await Promise.all(product.sides
    .filter(x => !x.renderer)
    .map(x => setRenderer(createCanvas({ width: 1, height: 1 }).canvas, x)))

  const missingContentSides = product.sides.map(x =>
    x.holders.length < 1 || x.holders.some(y =>
      y.type === "text"
        ? !y.actions.find(z => z.key === "text").value
        : !y.actions.find(z => z.key === "image").value))
  const missingContentCount = missingContentSides.filter(Boolean).length
  if (missingContentCount > 0) {
    product.sideIndex = missingContentSides.findIndex(Boolean)
    setDialog({
      title: "No content",
      description: `<p>${missingContentCount > 1 ? "Both sides are missing content" : "One side is missing content"}</p>`,
      actions: [{
        title: "Edit side",
        primary: true,
        fn: () => setDialog()
      }, {
        title: "Continue to cart",
        fn: () => {
          exportProduct(product)
          setDialog()
        }
      }]
    })
    m.redraw()
    return
  }

  await exportProduct(product)
}

export const toggleZoom = () => {
  state.zoom = !state.zoom
  setTimeout(() => {
    getSide().renderer.setScale().render()
  }, 100)
}

export const getZoom = () =>
  state.zoom

export const showContactDialog = () =>
  setDialog({
    title: "Do you need help?",
    description: `<div class="contact">
      <p><i class="material-icons">call</i> <a href="tel:+971555515625"><span>+971 5 555 15625</span></a></p>
      <p><i class="material-icons">email</i> <a href="mailto:support@armopro.ae"><span>support@armopro.ae</span></a></p>
      </div>`,
    actions: [{ title: "close", fn: () => setDialog() }]
  })

export const showDeleteDialog = (holder: IHolder) =>
  setDialog({
    title: "Are you sure?",
    description: "Do you really want to delete this content",
    actions: [{
      title: "no",
      fn: () => setDialog()
    }, {
      title: "yes",
      fn: () => {
        deleteHolder(holder)
        setDialog()
      }
    }]
  })

export const showImgQualityDialog = (holder: IHolder, dpi: number, img: HTMLImageElement) =>
  setDialog({
    type: "minimal",
    title: "Image quality is low",
    description: `<span>The quality of your image is too low for the current size. It will look <strong>${dpi < 100 ? "terrible" : dpi < 200 ? "bad" : "acceptable"}</strong> on your final product</span>`,
    actions: [{
      title: "Set recommended size",
      fn: () => {
        setDialog()
        setImageHolderRecommendedDimensions(holder, { dpi, img })
      }
    }, {
      title: "Continue",
      fn: () => setDialog()
    }]
  })

window["state"] = state
window["State"] = {
  ea,
  load,
  getUrlData,
  acceptedFileTypes,
  getCurrentHash,
  storeProduct,
  restoreProduct,
  clearProduct,
  isLoaded,
  getProduct,
  getSide,
  getHolder,
  getAction,
  setSide,
  setSidePrev,
  setSideNext,
  setProduct,
  setProductPrev,
  setProductNext,
  setDialog,
  getAlert,
  setAction,
  setHolder,
  addHolder,
  deleteHolder,
  createHolderBackup,
  restoreHolderBackup,
  setActionValuePassive,
  setActionValue,
  setActionValueAddition: setActionValueAsset,
  setActionState,
  setPosition,
  setRotation,
  setDepth,
  redraw,
  setRenderer,
  exportProduct,
  toCart,
  toggleZoom,
  getZoom,
  sideToImage,
  mRedraw: m.redraw,
}